"use client";

import { ArrowRight } from "lucide-react";
import Link from "next/link";

import { Button } from "@/lib/components/button";
import { NumberTicker } from "@/lib/components/number-ticker";

import { useI18n } from "@llmgateway/i18n/react";

import { selectEnterpriseText } from "./i18n";

interface StatCardProps {
	value: number;
	suffix?: string;
	prefix?: string;
	label: string;
	delay?: number;
}

function StatCard({
	value,
	suffix = "",
	prefix = "",
	label,
	delay,
}: StatCardProps) {
	return (
		<div className="flex flex-col items-center p-6 rounded-2xl border border-border bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300">
			<div className="text-2xl sm:text-3xl font-bold text-primary">
				{prefix}
				<NumberTicker value={value} delay={delay} className="text-primary" />
				{suffix}
			</div>
			<span className="mt-2 text-sm text-muted-foreground font-medium">
				{label}
			</span>
		</div>
	);
}

function toTickerStat(n: number): { value: number; suffix: string } {
	if (n >= 1_000_000_000) {
		return { value: Math.floor(n / 1_000_000_000), suffix: "B+" };
	}
	if (n >= 1_000_000) {
		return { value: Math.floor(n / 1_000_000), suffix: "M+" };
	}
	if (n >= 1_000) {
		return { value: Math.floor(n / 1_000), suffix: "K+" };
	}
	return { value: n, suffix: "" };
}

interface HeroEnterpriseProps {
	totalTokens?: number;
	totalRequests?: number;
}

export function HeroEnterprise({
	totalTokens = 100_000_000_000,
	totalRequests = 20_000_000,
}: HeroEnterpriseProps = {}) {
	const { locale } = useI18n();
	const text = selectEnterpriseText(locale).hero;
	const tokensStat = toTickerStat(totalTokens);
	const requestsStat = toTickerStat(totalRequests);

	return (
		<section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-4xl text-center">
					<Link
						href="/blog/soc2-type-ii"
						className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted px-4 py-1.5 transition-colors hover:border-blue-500/50"
					>
						<span className="text-xs font-mono text-blue-500">
							{text.badge}
						</span>
						<span className="text-xs text-muted-foreground">
							{text.badgeDetail}
						</span>
					</Link>
					<h1 className="mb-6 text-4xl font-bold tracking-tight text-balance sm:text-6xl lg:text-7xl">
						{text.title}
					</h1>
					<p className="mb-10 text-lg text-muted-foreground text-balance sm:text-xl max-w-3xl mx-auto leading-relaxed">
						{text.description}
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
						<Button size="lg" className="w-full sm:w-auto" asChild>
							<Link href="/enterprise#contact">
								{text.primaryCta}
								<ArrowRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
						<Button
							size="lg"
							variant="outline"
							className="w-full sm:w-auto bg-transparent"
							asChild
						>
							<Link href="/signup">{text.secondaryCta}</Link>
						</Button>
					</div>

					<div className="mt-16 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-6">
						<StatCard
							value={tokensStat.value}
							suffix={tokensStat.suffix}
							label={text.stats.totalTokens}
						/>
						<StatCard
							value={requestsStat.value}
							suffix={requestsStat.suffix}
							label={text.stats.totalRequests}
							delay={0.1}
						/>
						<StatCard
							value={200}
							suffix="M"
							label={text.stats.dailyTokens}
							delay={0.2}
						/>
						<StatCard
							value={60}
							suffix="K"
							prefix="$"
							label={text.stats.customerSavings}
							delay={0.3}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
